/**
 * DTC Overview Section - Enhanced for Executive Dashboard
 * 
 * Shows aggregated DTC performance across all platforms with drill-down
 * Designed to mirror Retail Partners section but with DTC-specific branding
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, ShoppingCart, Users, Package, DollarSign, ChevronDown, ChevronUp, ArrowRight, Info, RefreshCw, Database } from 'lucide-react';
import { Link } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import { DtcPlatformCard } from './DtcPlatformCard';
import { DtcGrowthTrends } from './DtcGrowthTrends';
import { useAggregateDtcSales } from '../lib/dtc-data-service';

interface DtcOverviewSectionProps {
  startDate: string;
  endDate: string;
}

export function DtcOverviewSection({ startDate: globalStartDate, endDate: globalEndDate }: DtcOverviewSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true); // Start expanded
  const [localDateRange, setLocalDateRange] = useState<'7' | '30' | '90' | null>('30'); // Default to 30 days
  const [aggregationMessage, setAggregationMessage] = useState<string | null>(null);
  const aggregateSales = useAggregateDtcSales();

  // DTC uses independent calendar dates, NOT fiscal week boundaries
  // Default to last 30 days ending TODAY
  const effectiveEndDate = React.useMemo(() => {
    if (localDateRange) {
      return new Date().toISOString().split('T')[0]; // Today
    }
    return globalEndDate;
  }, [globalEndDate, localDateRange]);
  
  const effectiveStartDate = React.useMemo(() => {
    if (localDateRange) {
      // User selected a specific range in DTC section - use TODAY as end date
      const end = new Date(); // Today
      const days = parseInt(localDateRange);
      const start = new Date(end);
      start.setDate(start.getDate() - days + 1);
      return start.toISOString().split('T')[0];
    }
    // Use global date range (fallback only)
    return globalStartDate;
  }, [globalStartDate, localDateRange]);

  // Fetch DTC KPIs with date range (aggregated across all platforms)
  const { data: dtcData, isLoading, error, refetch } = useQuery({
    queryKey: ['dtc-overview-kpis', effectiveStartDate, effectiveEndDate],
    queryFn: () => {
      const params = new URLSearchParams();
      params.append('start_date', effectiveStartDate);
      params.append('end_date', effectiveEndDate);
      console.log('[DtcOverviewSection] Fetching DTC KPIs:', { effectiveStartDate, effectiveEndDate });
      return apiClient.get(`/dtc/kpis?${params.toString()}`);
    },
    enabled: !!effectiveStartDate && !!effectiveEndDate,
  });
  
  const handleRefreshData = async () => {
    setAggregationMessage('Aggregating sales data from all platforms...');
    
    try {
      const result = await aggregateSales.mutateAsync({ days: 90, force: false });
      
      if (result.success) {
        setAggregationMessage(`✅ Data refreshed - Processed ${result.records_processed.amazon} Amazon records`);
        setTimeout(() => {
          setAggregationMessage(null);
          refetch();
        }, 3000);
      }
    } catch (error) {
      setAggregationMessage('❌ Failed to refresh: ' + (error instanceof Error ? error.message : 'Unknown error'));
      setTimeout(() => setAggregationMessage(null), 5000);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${(value * 100).toFixed(1)}%`;
  };
  
  const formatComparisonBadge = (growthPrev: number | null, growthYoy: number | null) => {
    // Show previous period comparison by default
    // If no prev data, show YoY
    // If neither, show N/A
    
    const hasPrev = growthPrev !== null && growthPrev !== undefined;
    const hasYoy = growthYoy !== null && growthYoy !== undefined;
    
    if (!hasPrev && !hasYoy) {
      return <span className="text-xs text-gray-400">N/A</span>;
    }
    
    const growth = hasPrev ? growthPrev : growthYoy;
    const label = hasPrev ? 'vs prev' : 'vs LY';
    const isPositive = growth! >= 0;
    
    return (
      <div className="flex items-center space-x-1 mt-2">
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
          isPositive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {isPositive ? '↑' : '↓'} {formatPercentage(growth!)}
        </span>
        <span className="text-xs text-gray-500">{label}</span>
        {hasPrev && hasYoy && (
          <span className={`text-xs ${growthYoy! >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            ({formatPercentage(growthYoy!)} YoY)
          </span>
        )}
      </div>
    );
  };

  // Defensive checks to prevent undefined errors when navigating back
  const summary = dtcData?.summary || {};
  const platforms = Array.isArray(dtcData?.by_platform) ? dtcData.by_platform : [];
  
  console.log('[DtcOverviewSection] Render state:', { isLoading, error, hasPlatforms: platforms.length > 0, dtcData });

  return (
    <section className="bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50 border-2 border-purple-200 rounded-2xl overflow-hidden shadow-lg">
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-6 hover:bg-purple-100/30 transition-colors flex items-center justify-between"
      >
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl shadow-md">
            <ShoppingBag className="w-7 h-7 text-white" />
          </div>
          <div className="text-left">
            <h2 className="text-2xl font-bold text-gray-900">DTC Sales Dashboard</h2>
            <p className="text-sm text-purple-700">
              B2C Direct Sales • What <strong>you</strong> make directly from customers
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          {/* Quick Stats when collapsed */}
          {!isExpanded && summary && (
            <div className="flex items-center space-x-6 mr-4">
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900">{formatCurrency(summary.total_revenue || 0)}</div>
                <div className="text-xs text-gray-600">Revenue</div>
        </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900">{platforms.length}</div>
                <div className="text-xs text-gray-600">Active Platforms</div>
      </div>
            </div>
          )}
          {isExpanded ? (
            <ChevronUp className="w-6 h-6 text-gray-600" />
          ) : (
            <ChevronDown className="w-6 h-6 text-gray-600" />
          )}
        </div>
      </button>

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="px-6 pb-6">
          {/* Data Refresh Section */}
          <div className="mb-4 bg-white border-2 border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h4 className="text-xs font-semibold text-gray-900 mb-0.5 flex items-center gap-2">
                  <Database className="w-3.5 h-3.5 text-purple-600" />
                  DTC Data Aggregation
                </h4>
                <p className="text-xs text-gray-600">
                  Combines data from all platforms (Shopify, Amazon, Walmart)
                </p>
                
                {aggregationMessage && (
                  <div className={`mt-2 px-3 py-1.5 rounded-md text-xs ${
                    aggregationMessage.includes('✅') ? 'bg-green-50 text-green-700 border border-green-200' :
                    aggregationMessage.includes('❌') ? 'bg-red-50 text-red-700 border border-red-200' :
                    'bg-blue-50 text-blue-700 border border-blue-200'
                  }`}>
                    {aggregationMessage}
                  </div>
                )}
              </div>
              
              <button
                onClick={handleRefreshData}
                disabled={aggregateSales.isPending}
                className="ml-3 px-3 py-1.5 bg-purple-600 text-white text-xs font-semibold rounded-lg shadow-sm hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all flex items-center gap-1.5"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${aggregateSales.isPending ? 'animate-spin' : ''}`} />
                {aggregateSales.isPending ? 'Refreshing...' : 'Refresh Data'}
              </button>
            </div>
          </div>

          {/* Date Range Selector */}
          <div className="mb-6 flex items-center justify-between bg-white border border-purple-200 rounded-lg p-3">
            <div className="flex items-center space-x-3">
              <ShoppingCart className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-medium text-gray-700">DTC Date Range:</span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setLocalDateRange(null)}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    localDateRange === null
                      ? 'bg-purple-600 text-white'
                      : 'bg-purple-50 text-gray-700 hover:bg-purple-100'
                  }`}
                >
                  Match Dashboard
                </button>
                <button
                  onClick={() => setLocalDateRange('7')}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    localDateRange === '7'
                      ? 'bg-purple-600 text-white'
                      : 'bg-purple-50 text-gray-700 hover:bg-purple-100'
                  }`}
                >
                  Last 7 Days
                </button>
                <button
                  onClick={() => setLocalDateRange('30')}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    localDateRange === '30'
                      ? 'bg-purple-600 text-white'
                      : 'bg-purple-50 text-gray-700 hover:bg-purple-100'
                  }`}
                >
                  Last 30 Days
                </button>
                <button
                  onClick={() => setLocalDateRange('90')}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    localDateRange === '90'
                      ? 'bg-purple-600 text-white'
                      : 'bg-purple-50 text-gray-700 hover:bg-purple-100'
                  }`}
                >
                  Last 90 Days
                </button>
              </div>
            </div>
            <div className="text-xs text-gray-600">
              {effectiveStartDate} to {effectiveEndDate}
            </div>
          </div>

          {/* Explainer Banner */}
          <div className="mb-6 bg-purple-100 border border-purple-300 rounded-lg p-3 flex items-start space-x-2">
            <Info className="w-4 h-4 text-purple-700 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-purple-800 leading-relaxed">
              <strong>Why separate from Retail?</strong> Retail metrics show "sell-out" (what retailers sell to end consumers - B2B2C). 
              DTC shows what you earn directly from customers (B2C). These represent different business models and aren't directly comparable.
            </p>
          </div>

          {isLoading ? (
            <div className="space-y-6 animate-pulse">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-32 bg-white/50 rounded-xl" />
                ))}
            </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-48 bg-white/50 rounded-xl" />
                ))}
          </div>
            </div>
          ) : (
            <>
              {/* Summary KPI Cards - Simplified 4-card row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {/* Total Revenue */}
                <div className="bg-white rounded-xl p-5 border border-purple-200 shadow-sm">
                  <div className="p-2 bg-purple-100 rounded-lg w-fit mb-3">
                    <DollarSign className="w-5 h-5 text-purple-600" />
          </div>
                  <div className="text-3xl font-bold text-gray-900">
                    {formatCurrency(summary.total_revenue || 0)}
          </div>
                  <div className="text-sm text-gray-600 mt-1">Total Revenue</div>
                  {formatComparisonBadge(summary.revenue_growth_prev, summary.revenue_growth_yoy)}
        </div>

                {/* Total Orders */}
                <div className="bg-white rounded-xl p-5 border border-purple-200 shadow-sm">
                  <div className="p-2 bg-pink-100 rounded-lg w-fit mb-3">
                    <ShoppingCart className="w-5 h-5 text-pink-600" />
            </div>
                  <div className="text-3xl font-bold text-gray-900">
                    {formatNumber(summary.total_orders || 0)}
          </div>
                  <div className="text-sm text-gray-600 mt-1">Total Orders</div>
                  {formatComparisonBadge(summary.orders_growth_prev, summary.orders_growth_yoy)}
            </div>

                {/* Avg Order Value */}
                <div className="bg-white rounded-xl p-5 border border-purple-200 shadow-sm">
                  <div className="p-2 bg-indigo-100 rounded-lg w-fit mb-3">
                    <Package className="w-5 h-5 text-indigo-600" />
          </div>
                  <div className="text-3xl font-bold text-gray-900">
                    {formatCurrency(summary.avg_order_value || 0)}
          </div>
                  <div className="text-sm text-gray-600 mt-1">Avg Order Value</div>
                  {formatComparisonBadge(summary.aov_growth_prev, summary.aov_growth_yoy)}
                </div>

                {/* Total Units */}
                <div className="bg-white rounded-xl p-5 border border-purple-200 shadow-sm">
                  <div className="p-2 bg-fuchsia-100 rounded-lg w-fit mb-3">
                    <Package className="w-5 h-5 text-fuchsia-600" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900">
                    {formatNumber(summary.total_units || 0)}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Total Units</div>
                  {formatComparisonBadge(summary.units_growth_prev, summary.units_growth_yoy)}
                </div>
              </div>
              
              {/* Platform Performance Cards */}
              {Array.isArray(platforms) && platforms.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Platform Performance</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {platforms.map((platform: any) => {
                      if (!platform || !platform.platform_id) return null;
                      const totalRevenue = summary?.total_revenue || 0;
                      const percentageOfTotal = totalRevenue > 0 
                        ? ((platform.revenue || 0) / totalRevenue) * 100 
                        : 0;

                      return (
                        <DtcPlatformCard
                          key={platform.platform_id}
                          platformId={platform.platform_id}
                          platformName={platform.platform_name}
                          platformType={platform.platform_type}
                          revenue={platform.revenue || 0}
                          orders={platform.orders || 0}
                          avgOrderValue={platform.avg_order_value || 0}
                          units={platform.units || 0}
                          percentageOfTotal={percentageOfTotal}
                          revenue_growth_prev={platform.revenue_growth_prev}
                          revenue_growth_yoy={platform.revenue_growth_yoy}
                          orders_growth_prev={platform.orders_growth_prev}
                          orders_growth_yoy={platform.orders_growth_yoy}
                          aov_growth_prev={platform.aov_growth_prev}
                          aov_growth_yoy={platform.aov_growth_yoy}
                          units_growth_prev={platform.units_growth_prev}
                          units_growth_yoy={platform.units_growth_yoy}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* DTC Revenue Trend Chart */}
              <div className="mb-6">
                <DtcGrowthTrends 
                  startDate={effectiveStartDate}
                  endDate={effectiveEndDate}
                />
              </div>

              {/* View Full DTC Analytics Link */}
              <div className="text-center pt-4 border-t border-purple-200">
                <Link href="/dtc/overview">
                  <a className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-md hover:shadow-lg">
                    <ShoppingBag className="w-5 h-5" />
                    <span>View Full DTC Analytics</span>
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </Link>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
